"use client";

import { tsv } from "@faire/slate/lib/tsv";
import React, { useEffect, useState } from "react";

import { AvatarSize } from "./types";

type IProps = {
  /**
   * The border color of the avatar
   */
  borderColor?: string;

  /**
   * Container className
   */
  className?: string;

  /**
   * The image url of the avatar
   */
  imageUrl?: string;
  /**
   * The label of the avatar
   */
  label?: string;

  /**
   * The size of the avatar
   */
  size?: AvatarSize;

  /**
   * The data component name. Used for adoption tracking.
   */
  "data-component-name"?: string;
};

export const AvatarBase: React.FC<React.PropsWithChildren<IProps>> = ({
  label,
  size,
  imageUrl,
  borderColor,
  children,
  className,
  "data-component-name": dataComponentName,
}) => {
  const [imageError, setImageError] = useState(false);

  // Reset error state when image URL changes
  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  const containerCls = containerStyle.cls({ size, className });
  const containerVars = containerStyle.vars({
    borderColor,
    backgroundColor:
      imageUrl && !imageError
        ? "var(--fs-avatar-background-with-image)"
        : "var(--fs-avatar-background-without-image)",
  });

  return (
    <span
      className={containerCls}
      style={containerVars}
      data-component-name={dataComponentName}
    >
      {imageUrl && !imageError ? (
        <>
          <img
            className="h-full w-full rounded-[inherit] object-cover"
            src={imageUrl}
            alt={label ?? ""}
            onError={() => setImageError(true)}
          />
          <span
            data-test-id="avatar-image-overlay"
            className="pointer-events-none absolute inset-0 rounded-[inherit] bg-(--fs-avatar-image-overlay)"
          />
        </>
      ) : (
        <label>{label?.[0]?.toUpperCase()}</label>
      )}
      {children}
    </span>
  );
};

const containerStyle = tsv({
  vars: {
    borderColor: "fs-avatar-border-color",
    backgroundColor: "fs-avatar-background-color",
  },
  base: ["fs-avatar fs-avatar__placeholder text-(--fs-avatar-text-color)"],
  variants: {
    size: {
      xxSmall: "type-fs-subheading-medium size-(--slate-dimensions-8)",
      xSmall: "type-fs-section-heading size-(--slate-dimensions-10)",
      small: "type-fs-section-heading size-(--slate-dimensions-12)",
      medium: "type-fs-section-heading size-(--slate-dimensions-14)",
      large: "type-fs-section-heading size-(--slate-dimensions-16)",
      xLarge: "type-fs-display-s-sans-semibold h-24 w-24",
    } satisfies Record<AvatarSize, string>,
  },
});
